import { Module, Action, VuexModule, Mutation } from "vuex-module-decorators";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import Echo from "laravel-echo";
import "pusher-js";
import { Notification } from "@/core/services/api/interfaces/manager/Notification";
import { SweetAlertOptions, SweetAlertResult } from "sweetalert2";
import JwtService from "@/core/services/JwtService";
import UserService from "@/core/services/UserService";
import downloader, {
  DownloaderService,
} from "@/core/services/DownloaderService";
import { defaultOptions as SwalDefaultOptions } from "@/plugins/swal";
import Swal from "sweetalert2";
import {
  ImportItem,
  ImportStatus,
} from "@/core/services/api/interfaces/manager/ImportItem";
import {
  isSourceOfFundsWithAction,
  isNegativeBalance,
} from "@/helpers/notification";
import ApiService from "@/core/services/ApiService";
import { Transaction } from "@/core/services/api/interfaces/manager/Transaction";
import store from "@/store";
import { isModalShown } from "@/core/helpers/dom";
import router from "@/router";
import { getWindowIdentifier } from "@/core/services/WindowIdentifier";
import { BankAccount } from "@/core/services/api/interfaces/manager/BankAccount";
import { Company } from "@/core/services/api/interfaces/common/Company";
import { User } from "@/core/services/api/interfaces/manager/User";
import { NotificationType } from "@/core/services/api/enums/NotificationType";
import { CreateTransactionAction } from "@/core/services/api/enums/CreateTransactionAction";
import { TransactionMethod } from "@/core/services/api/enums/TransactionMethod";
import { ComplianceTracking } from "@/core/services/api/interfaces/manager/ComplianceTracking";
import { KYCFile } from "@/core/services/api/interfaces/manager/KYCFile";

const options = {
  broadcaster: process.env.VUE_APP_WS_BROADCASTER,
  key: process.env.VUE_APP_WS_KEY,
  wsHost: process.env.VUE_APP_WS_HOST,
  wsPort: process.env.VUE_APP_WS_PORT,
  authEndpoint: `${process.env.VUE_APP_API_URL}${process.env.VUE_APP_AUTH_ENDPOINT}`,
  forceTLS: false,
  disableStats: true,
};

const swal = Swal.mixin(SwalDefaultOptions) as typeof Swal;

@Module
export default class WebSocketModule extends VuexModule {
  _echo: Echo | null = null;

  get isWsEchoSet(): boolean {
    return !!this._echo;
  }

  get wsSwal(): typeof Swal {
    return swal;
  }

  get wsEcho(): null | Echo {
    return this._echo;
  }

  get downloader(): DownloaderService {
    return downloader;
  }

  get options() {
    return (token: string | null) => {
      if (!token) {
        return options;
      }

      return Object.assign(options, {
        auth: {
          headers: { Authorization: `Bearer ${token}` },
        },
      });
    };
  }

  @Mutation
  [Mutations.COMMON_WEBSOCKETS_ECHO_SET](echo: Echo | null) {
    this._echo = echo;
  }

  @Action({ rawError: true })
  [Actions.COMMON_WEBSOCKETS_CLOSE]() {
    console.log(Actions.COMMON_WEBSOCKETS_CLOSE, this._echo);
    if (!this._echo) {
      return;
    }
    this._echo.disconnect();
    this.context.commit(Mutations.COMMON_WEBSOCKETS_ECHO_SET, null);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_WEBSOCKETS_INITIATE]() {
    console.log(Actions.COMMON_WEBSOCKETS_INITIATE, "started");
    await this.context
      .dispatch(Actions.COMMON_WEBSOCKETS_CLOSE)
      .catch((e) => console.error(e));
    console.log(Actions.COMMON_WEBSOCKETS_INITIATE, "continue");
    const token = JwtService.getToken();
    const echo = new Echo(this.options(token?.access_token || null));
    const user = UserService.getUser();
    if (user?.company?.id) {
      echo
        .private(`company.${user.company.id}`)
        .listen(".import.created", (importItem: ImportItem) => {
          const toastOptions = {
            title: `Import job #${importItem.id} created`,
            icon: "success",
            text: "Processing will begin shortly",
          } as SweetAlertOptions;

          return this.wsSwal.fire(toastOptions);
        })
        .listen(".compliance.created", (compliance: ComplianceTracking) => {
          store.commit(
            Mutations.MANAGER_COMPLIANCE_TRACKINGS_REPLACE,
            compliance
          );

          store
            .dispatch(Actions.MANAGER_COMPANY_ALERTS_LOAD)
            .catch(console.error);
        })
        .listen(".compliance.updated", (compliance: ComplianceTracking) => {
          store.commit(
            Mutations.MANAGER_COMPLIANCE_TRACKINGS_REPLACE,
            compliance
          );

          store
            .dispatch(Actions.MANAGER_COMPANY_ALERTS_LOAD)
            .catch(console.error);
        })
        .listen(".kyc_file.created", (file: KYCFile) => {
          store.commit(
            Mutations.MANAGER_COMPLIANCE_TRACKINGS_FILE_REPLACE,
            file
          );
        })
        .listen(".kyc_file.updated", (file: KYCFile) => {
          store.commit(
            Mutations.MANAGER_COMPLIANCE_TRACKINGS_FILE_REPLACE,
            file
          );
        })
        .listen(".import.updated", (importItem: ImportItem) => {
          if (ImportStatus.DONE === importItem.status) {
            this.wsSwal
              .fire({
                timer: 3000,
                title: `Import job #${importItem.id} finished successfully!`,
                icon: "success",
                text: 'Imported transactions can be found on "Pending transactions" page',
              })
              .then(() => {
                console.log("then works!");
                return router.push({
                  name: "manager.transactions.list",
                  params: { mode: "pending_transactions" },
                });
              });
          } else if (ImportStatus.ERROR === importItem.status) {
            if (this.downloader.isDownloadableImportErrors(importItem)) {
              this.wsSwal
                .fire({
                  title: `Import job #${importItem.id} finished with errors.`,
                  icon: "error",
                  text: "You can download file with errors to find out what went wrong",
                  showConfirmButton: true,
                  confirmButtonText: "Download Errors",
                  confirmButtonColor: "#F64E60",
                })
                .then((e: SweetAlertResult) => {
                  if (e.isConfirmed) {
                    this.downloader.downloadImportErrors(importItem);
                  }
                });
            } else {
              this.wsSwal.fire({
                title: `Import job #${importItem.id} failed.`,
                icon: "error",
                background: "#F64E60",
                color: "white",
                text: "Contact support please to resolve this problem",
                showConfirmButton: false,
              });
            }
          } else if (ImportStatus.PROCESSING === importItem.status) {
            this.wsSwal.fire({
              title: `Import job #${importItem.id} processing started!`,
              icon: "success",
              text: "You will be notified when the processing is done.",
            });
          }

          if (this.downloader.isDownloadableImportErrors(importItem)) {
            return this.downloader.downloadImportErrors(importItem);
          }
        })
        .listen(".company.updated", (company: Company) => {
          console.log("company: updated", company);
          store.dispatch(Actions.COMMON_USER_COMPANY_SET, company);
        })
        .listen(".account.created", (account: BankAccount) => {
          console.log("bank account: created", account);
          store.commit(
            Mutations.MANAGER_COMPANY_ACCOUNT_REPLACE_OR_ADD,
            account
          );
        })
        .listen(".user.updated", (user: User) => {
          console.log("user: updated", user);
          store.commit(Mutations.COMMON_USER_SET, user);
        })
        .listen(".account.created", (account: BankAccount) => {
          console.log("bank account: created", account);
          store.commit(
            Mutations.MANAGER_COMPANY_ACCOUNT_REPLACE_OR_ADD,
            account
          );
        })
        .listen(".account.updated", (account: BankAccount) => {
          console.log("bank account: updated", account);
          store.commit(
            Mutations.MANAGER_COMPANY_ACCOUNT_REPLACE_OR_ADD,
            account
          );
        })
        .listen(".notification.created", async (notification: Notification) => {
          await store.dispatch(Actions.COMMON_NOTIFICATIONS_LOAD);
          const subject = notification.notificationable;
          const toastOptions = {
            title: notification.title,
            icon: "success",
            text: notification.message,
          } as SweetAlertOptions;
          const isDownloadable =
            this.downloader.isDownloadableNotification(notification);
          const isSourceOfFundsMissing =
            isSourceOfFundsWithAction(notification);
          const isKyc =
            subject && "service" in subject && "files" === subject.service;
          const isRejectedType =
            notification.type ===
            NotificationType.COMPLIANCE_TRACKING_DOCUMENTS_REJECTED;
          const isBankAccountNegativeBalance = isNegativeBalance(notification);
          console.log(notification);
          if (isBankAccountNegativeBalance) {
            toastOptions.showConfirmButton = true;
            toastOptions.icon = "warning";
            toastOptions.confirmButtonText = "Fix the issue";
          }
          if (isDownloadable) {
            toastOptions.showConfirmButton = true;
            toastOptions.confirmButtonText = "Download";
            toastOptions.confirmButtonColor = "#009EF7";
          }
          if (isSourceOfFundsMissing) {
            toastOptions.showConfirmButton = true;
            toastOptions.confirmButtonText = "Set source of funds";
            toastOptions.confirmButtonColor = "#009EF7";
          }
          if (isRejectedType) {
            toastOptions.icon = "error";
          }
          if (isKyc) {
            toastOptions.showConfirmButton = true;
            toastOptions.confirmButtonText = "Upload files";
            toastOptions.confirmButtonColor = "#009EF7";
          }
          this.wsSwal.fire(toastOptions).then((e: SweetAlertResult) => {
            if (!e.isConfirmed) {
              return;
            }
            if (isDownloadable) {
              this.downloader.downloadNotification(notification);
            }
            if (isBankAccountNegativeBalance) {
              return router.push({
                name: "manager.transactions.create",
                params: {
                  action: CreateTransactionAction.CREATE,
                  method: TransactionMethod.TRANSFER,
                },
                query: {
                  tid: subject.id,
                },
              });
            }
            if (
              isKyc &&
              "service" in subject &&
              "compliable_id" in subject &&
              "compliable_type" &&
              subject &&
              subject.compliable_type &&
              subject.compliable_id
            ) {
              return router.push({
                name: "manager.compliance-trackings.list",
                params: {
                  entity: subject.compliable_type,
                  entity_id: subject.compliable_id,
                  kyc_id: subject.id,
                },
              });
            }
            if (isSourceOfFundsMissing) {
              ApiService.setTransactionSourceOfFunds(
                notification.notificationable as Transaction
              ).then(() => store.dispatch(Actions.COMMON_NOTIFICATIONS_LOAD));
            }
          });
        });
    }

    echo.channel("announce").listen(".auth_code", ({ otp, identifier }) => {
      console.log(getWindowIdentifier(), identifier, "X-WI");
      if (getWindowIdentifier() !== identifier) {
        return;
      }
      console.log(otp);
      const modal = document.getElementById("auth-code-modal");
      const otpInput = document.getElementById("auth_code");

      this.wsSwal
        .fire({
          title: `Auth code`,
          icon: "success",
          html: `Auth code is <span id="auth_code_block">${otp}</span>`,
          confirmButtonText: "Submit",
          denyButtonText: "Copy",
          showConfirmButton: !!modal && !!otpInput && isModalShown(modal),
          showDenyButton: true,
          customClass: {
            confirmButton: "btn btn-primary",
            denyButton: "btn btn-primary",
          },
        })
        .then((mode) => {
          if (mode.isDenied) {
            return navigator.clipboard.writeText(otp);
          }
          if (mode.isConfirmed) {
            store.commit(Mutations.COMMON_AUTH_TFA_CODE_SET, otp);
          }
        });
    });

    this.context.commit(Mutations.COMMON_WEBSOCKETS_ECHO_SET, echo);
  }
}
